import Alert from '../Alert';
import Button from '../Button';
import Checkbox from '../Checkbox';
import Icon from '../Icon';
import Link from '../Link';
import PropTypes from 'prop-types';
import LoadingButton from '../LoadingButton';
import Textbox from '../Textbox';
import { Form } from 'reactstrap';
import React, { useEffect, useState } from 'react';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useQueryString } from '../../hooks/useQueryString';

import Wrapper from './Wrapper';
import has from 'lodash/has';
import styles from './styles.module.scss';
import { useForm } from 'react-hook-form';

const Login = ({
  submitting,
  error,
  allowRememberUser = true,
  handleLogin = () => {},
}) => {
  useDocumentTitle('Emotive Login');
  const values = useQueryString();
  const { next, referrer } = values;

  // Validate and 'whitelist' referrer to ensure we only allow
  // requests from the embedded app host
  const isShopifyEmbedded =
    referrer && referrer === process.env.SHOPIFY_EMBEDDED_HOST;

  const [shopifyLogin, setShopifyLogin] = useState(false);
  const [rememberUser, setRememberUser] = useState(false);

  // Using onSubmit since validation errors would show briefly with onBlur when transitioning between log in types
  const { register, handleSubmit, errors, reset } = useForm({
    mode: 'onSubmit',
  });

  useEffect(() => reset(true), [reset]);

  const toggleLogin = () => setShopifyLogin(!shopifyLogin);

  const onSubmit = (data) => {
    const { username, password, shopify } = data;

    if (shopifyLogin) {
      window.location.href = `/login/shopify/auth?shop=${shopify}`;
    } else {
      handleLogin(username, password, rememberUser, next, isShopifyEmbedded);
    }
  };
  const text = `Don't have an account yet? `;
  return (
    <Wrapper>
      <h2 className="mb-3">Log in to your account</h2>
      <p className="mb-4 fs-16">
        {text}
        <Link
          href="https://emotive.io/?show_demo=true&utm_content=HelloBar"
          target="_blank"
        >
          Schedule a demo.
        </Link>
      </p>
      {!isShopifyEmbedded && (
        <Button
          className="btn-block"
          data-testid="login-with-option-button"
          onClick={() => toggleLogin()}
          outline={true}
        >
          {shopifyLogin ? (
            <Icon name="user" variant="regular" />
          ) : (
            <Icon name="shopify" variant="brand" />
          )}
          <span className="pl-2">
            {shopifyLogin ? 'Log in with Username' : 'Log in with Shopify'}
          </span>
        </Button>
      )}
      <Form onSubmit={handleSubmit(onSubmit)} data-testid="login-form">
        {shopifyLogin ? (
          <Textbox
            autoFocus
            className="mt-4"
            data-testid="login-form-shopify-input"
            error={has(errors, 'shopify')}
            feedback="A Shopify domain is required"
            icon="user"
            id="shopify"
            name="shopify"
            innerRef={register({ required: true })}
            placeholder="Shopify domain"
            suffix=".myshopify.com"
          />
        ) : (
          <div key={shopifyLogin}>
            {!shopifyLogin && !isShopifyEmbedded && (
              <div className={styles.divider}>OR</div>
            )}
            {error && (
              <Alert severity="error">
                <div className="text-center">
                  <span className="text-error-dark pr-2">
                    <Icon name="exclamation-square" variant="solid" />
                  </span>
                  Please enter a correct username and password. Note that both
                  fields may be case-sensitive.
                </div>
              </Alert>
            )}
            <Textbox
              autoFocus
              data-testid="login-form-username-input"
              error={has(errors, 'username')}
              feedback="Username is required"
              icon="user"
              id="username"
              innerRef={register({ required: true })}
              name="username"
              placeholder="Username"
            />
            <Textbox
              data-testid="login-form-password-input"
              error={has(errors, 'password')}
              feedback="Password is required"
              icon="lock"
              id="password"
              innerRef={register({ required: true })}
              name="password"
              placeholder="Password"
              type="password"
            />
            {allowRememberUser && (
              <Checkbox
                data-testid="login-form-remember-me"
                className="mt-3"
                checkboxPosition="left"
                id="remember_me"
                label="Remember Me"
                name="remember_me"
                onChange={(e) => setRememberUser(e.target.checked)}
                value={rememberUser}
              />
            )}
          </div>
        )}

        <LoadingButton
          block
          loading={submitting}
          error={error}
          className="mt-4 mb-3"
          color="blueberry"
          type="submit"
          data-testid="login-form-submit-button"
        >
          Log In
        </LoadingButton>

        {!shopifyLogin && (
          <div className="text-center fs-14">
            <Link
              data-testid="login-form-forgot-password-link"
              to="/login/reset"
            >
              Forgot password?
            </Link>
          </div>
        )}
      </Form>
    </Wrapper>
  );
};

Login.propTypes = {
  submitting: PropTypes.bool,
  error: PropTypes.bool,
  allowRememberUser: PropTypes.bool,
  handleLogin: PropTypes.func,
  children: PropTypes.node,
};

export default Login;
